import { useState, useEffect } from 'react';
import api from '../services/api';
import { Buildings, Plus, X, PencilSimple, Power, Users, Receipt, Crown, EnvelopeSimple, FloppyDisk, UserPlus, CheckCircle, Circle, Info, Spinner, PaperPlaneTilt, ListChecks } from '@phosphor-icons/react';

export default function SuperAdminMandanten() {
  const [mandanten, setMandanten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [userMandant, setUserMandant] = useState(null);
  const [onboardingMandant, setOnboardingMandant] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/mandanten');
      setMandanten(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (m) => {
    if (m.active) {
      if (!window.confirm(`Mandant "${m.name}" wirklich deaktivieren?`)) return;
      await api.delete(`/admin/mandanten/${m.id}`);
    } else {
      await api.put(`/admin/mandanten/${m.id}`, { active: true });
    }
    load();
  };

  return (
    <div className="space-y-6" data-testid="sa-mandanten-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Crown weight="fill" className="w-8 h-8 text-amber-400" />
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-slate-900">
              Mandanten
            </h1>
            <p className="text-slate-500 mt-1 text-sm">Alle Mandanten der Plattform verwalten</p>
          </div>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="bg-violet-700 text-white px-6 py-2.5 font-medium hover:bg-violet-800 flex items-center justify-center gap-2"
          data-testid="sa-create-mandant-button"
        >
          <Plus weight="bold" className="w-4 h-4" />
          Neuer Mandant
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-700"></div>
        </div>
      ) : mandanten.length === 0 ? (
        <div className="bg-white border border-gray-200 p-12 text-center">
          <Buildings weight="light" className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Noch keine Mandanten – legen Sie den ersten an.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {mandanten.map(m => (
            <div
              key={m.id}
              className={`bg-white border ${m.active ? 'border-gray-200' : 'border-amber-300 opacity-75'} p-5 space-y-4`}
              data-testid={`mandant-card-${m.id}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-heading text-xl font-black text-slate-900">{m.name}</h3>
                  {m.kontakt_email && (
                    <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                      <EnvelopeSimple weight="regular" className="w-3 h-3" /> {m.kontakt_email}
                    </p>
                  )}
                  {m.notes && <p className="text-xs text-slate-400 mt-1 italic">{m.notes}</p>}
                </div>
                <span className={`text-xs px-2 py-1 font-medium ${m.active ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                  {m.active ? 'Aktiv' : 'Inaktiv'}
                </span>
              </div>

              <div className="grid grid-cols-4 gap-2 border-t border-gray-100 pt-3">
                <div className="text-center">
                  <p className="font-mono text-lg font-bold text-slate-900">{m.stats.properties}</p>
                  <p className="text-xs text-slate-500">Immobilien</p>
                </div>
                <div className="text-center">
                  <p className="font-mono text-lg font-bold text-slate-900">{m.stats.mieter}</p>
                  <p className="text-xs text-slate-500">Mieter</p>
                </div>
                <div className="text-center">
                  <p className="font-mono text-lg font-bold text-slate-900">{m.stats.users}</p>
                  <p className="text-xs text-slate-500">User</p>
                </div>
                <div className="text-center">
                  <p className="font-mono text-lg font-bold text-slate-900">{m.stats.receipts}</p>
                  <p className="text-xs text-slate-500">Belege</p>
                </div>
              </div>

              {/* Onboarding-Progress */}
              {m.onboarding && (
                <button
                  type="button"
                  onClick={() => setOnboardingMandant(m)}
                  className="w-full text-left border-t border-gray-100 pt-3 group"
                  data-testid={`mandant-onboarding-${m.id}`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-bold text-slate-600 uppercase tracking-wide flex items-center gap-1">
                      <ListChecks weight="bold" className="w-3.5 h-3.5" /> Onboarding
                    </span>
                    <span className={`text-xs font-bold ${m.onboarding.fertig ? 'text-emerald-600' : 'text-slate-700'}`}>
                      {m.onboarding.done}/{m.onboarding.total} · {m.onboarding.progress}%
                      {m.onboarding.fertig && ' ✓'}
                    </span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${m.onboarding.fertig ? 'bg-emerald-500' : m.onboarding.progress >= 50 ? 'bg-amber-500' : 'bg-red-400'}`}
                      style={{ width: `${m.onboarding.progress}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1 group-hover:text-violet-700">Details öffnen →</p>
                </button>
              )}

              <div className="flex gap-2 pt-3 border-t border-gray-100">
                <button
                  onClick={() => setUserMandant(m)}
                  className="flex-1 text-xs border border-slate-300 text-slate-700 px-3 py-2 hover:bg-slate-50 flex items-center justify-center gap-1.5"
                  data-testid={`mandant-users-${m.id}`}
                >
                  <UserPlus weight="bold" className="w-3.5 h-3.5" /> User
                </button>
                <button
                  onClick={() => setEditing(m)}
                  className="flex-1 text-xs border border-slate-300 text-slate-700 px-3 py-2 hover:bg-slate-50 flex items-center justify-center gap-1.5"
                  data-testid={`mandant-edit-${m.id}`}
                >
                  <PencilSimple weight="bold" className="w-3.5 h-3.5" /> Bearbeiten
                </button>
                <button
                  onClick={() => toggleActive(m)}
                  className={`flex-1 text-xs px-3 py-2 flex items-center justify-center gap-1.5 ${m.active ? 'border border-amber-300 text-amber-700 hover:bg-amber-50' : 'border border-emerald-300 text-emerald-700 hover:bg-emerald-50'}`}
                  data-testid={`mandant-toggle-${m.id}`}
                >
                  <Power weight="bold" className="w-3.5 h-3.5" /> {m.active ? 'Deaktivieren' : 'Aktivieren'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && <MandantFormModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />}
      {editing && <MandantFormModal mandant={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {userMandant && <MandantUsersModal mandant={userMandant} onClose={() => setUserMandant(null)} onSaved={load} />}
      {onboardingMandant && <OnboardingModal mandant={onboardingMandant} onClose={() => setOnboardingMandant(null)} onSaved={load} />}
    </div>
  );
}

function MandantFormModal({ mandant, onClose, onSaved }) {
  const isEdit = !!mandant;
  const [form, setForm] = useState({
    name: mandant?.name || '',
    kontakt_email: mandant?.kontakt_email || '',
    max_users: mandant?.max_users || 5,
    max_properties: mandant?.max_properties || 20,
    notes: mandant?.notes || '',
    active: mandant?.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (isEdit) {
        await api.put(`/admin/mandanten/${mandant.id}`, form);
      } else {
        await api.post('/admin/mandanten', form);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="mandant-form-modal">
      <div className="bg-white w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="font-heading text-lg font-bold text-slate-900">{isEdit ? 'Mandant bearbeiten' : 'Neuer Mandant'}</h2>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600"><X weight="bold" className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-800 p-3 text-sm">{error}</div>}

          <div>
            <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
              className="w-full border-b border-slate-300 bg-transparent py-2 focus:border-slate-900 outline-none"
              required
              data-testid="mandant-name-input"
            />
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Kontakt E-Mail</label>
            <input
              type="email"
              value={form.kontakt_email}
              onChange={(e) => setForm(p => ({ ...p, kontakt_email: e.target.value }))}
              className="w-full border-b border-slate-300 bg-transparent py-2 focus:border-slate-900 outline-none"
              data-testid="mandant-email-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Max. User</label>
              <input
                type="number"
                min="1"
                value={form.max_users}
                onChange={(e) => setForm(p => ({ ...p, max_users: parseInt(e.target.value) || 1 }))}
                className="w-full border-b border-slate-300 bg-transparent py-2 focus:border-slate-900 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Max. Immobilien</label>
              <input
                type="number"
                min="1"
                value={form.max_properties}
                onChange={(e) => setForm(p => ({ ...p, max_properties: parseInt(e.target.value) || 1 }))}
                className="w-full border-b border-slate-300 bg-transparent py-2 focus:border-slate-900 outline-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Notizen</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))}
              className="w-full border border-slate-300 bg-transparent p-2 focus:border-slate-900 outline-none text-sm"
              rows="2"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-200 text-slate-900 py-2.5 font-medium hover:bg-gray-50">Abbrechen</button>
            <button type="submit" disabled={saving} className="flex-1 bg-violet-700 text-white py-2.5 font-medium hover:bg-violet-800 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="mandant-save-button">
              <FloppyDisk weight="bold" className="w-4 h-4" /> {saving ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function MandantUsersModal({ mandant, onClose, onSaved }) {
  const [users, setUsers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', role: 'admin' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);
  const [copiedField, setCopiedField] = useState('');

  const load = async () => {
    const res = await api.get(`/admin/mandanten/${mandant.id}/users`);
    setUsers(res.data);
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(''), 2000);
  };

  const addUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      // password is auto-generated by backend if not provided
      const res = await api.post('/admin/users', {
        email: form.email,
        name: form.name,
        role: form.role,
        mandant_id: mandant.id,
        send_welcome_email: true,
      });
      setCreatedUser(res.data);
      setForm({ email: '', name: '', role: 'admin' });
      setShowAdd(false);
      load();
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="mandant-users-modal">
      <div className="bg-white w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <div>
            <h2 className="font-heading text-lg font-bold text-slate-900">User des Mandanten</h2>
            <p className="text-xs text-slate-500">{mandant.name}</p>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600"><X weight="bold" className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          {/* Show credentials after creation */}
          {createdUser && (
            <div className="bg-emerald-50 border-2 border-emerald-300 p-4 space-y-3" data-testid="created-user-credentials">
              <div className="flex items-center justify-between">
                <p className="font-bold text-emerald-900">✓ User angelegt – Zugangsdaten</p>
                <button onClick={() => setCreatedUser(null)} className="text-emerald-700 hover:text-emerald-900 text-xs">Schließen</button>
              </div>
              <p className="text-xs text-emerald-800">
                {createdUser.email_sent && createdUser.email_note
                  ? '⚠ ' + createdUser.email_note + ' – Bitte kopieren Sie die Daten manuell:'
                  : createdUser.email_sent
                    ? '✓ Begrüßungs-E-Mail wurde versendet. Zur Sicherheit auch hier:'
                    : '⚠ E-Mail konnte nicht versendet werden:'}
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold w-20 text-emerald-900">E-Mail:</span>
                  <code className="flex-1 bg-white border border-emerald-200 px-2 py-1 text-sm font-mono">{createdUser.email}</code>
                  <button onClick={() => copyToClipboard(createdUser.email, 'email')} className="text-xs text-emerald-700 hover:text-emerald-900 underline" data-testid="copy-email">
                    {copiedField === 'email' ? '✓' : 'Kopieren'}
                  </button>
                </div>
                {createdUser.generated_password && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold w-20 text-emerald-900">Passwort:</span>
                    <code className="flex-1 bg-white border border-emerald-200 px-2 py-1 text-sm font-mono">{createdUser.generated_password}</code>
                    <button onClick={() => copyToClipboard(createdUser.generated_password, 'pw')} className="text-xs text-emerald-700 hover:text-emerald-900 underline" data-testid="copy-password">
                      {copiedField === 'pw' ? '✓' : 'Kopieren'}
                    </button>
                  </div>
                )}
              </div>
              <p className="text-xs text-emerald-700 italic">Der User wird beim ersten Login zum Passwort-Wechsel aufgefordert.</p>
            </div>
          )}

          {users.length > 0 ? (
            <div className="space-y-2">
              {users.map(u => (
                <div key={u.id} className="flex items-center justify-between border border-gray-200 px-3 py-2">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{u.name}</p>
                    <p className="text-xs text-slate-500">{u.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 ${u.role === 'super_admin' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}`}>{u.role}</span>
                    <button
                      onClick={async () => {
                        const pw = window.prompt(`Neues Passwort für ${u.email} (mind. 8 Zeichen):`);
                        if (!pw) return;
                        if (pw.length < 8) { alert('Passwort muss mindestens 8 Zeichen haben'); return; }
                        try {
                          await api.post('/auth/admin/reset-password', { user_id: u.id, new_password: pw });
                          alert(`✓ Passwort für ${u.email} wurde zurückgesetzt.\n\nNeue Login-Daten:\nE-Mail: ${u.email}\nPasswort: ${pw}\n\nBitte sicher weitergeben.`);
                        } catch (err) {
                          alert('Fehler: ' + (err.response?.data?.detail || err.message));
                        }
                      }}
                      className="text-xs px-2 py-1 border border-amber-200 bg-amber-50 text-amber-800 rounded hover:bg-amber-100"
                      title="Passwort zurücksetzen"
                      data-testid={`reset-pw-user-${u.id}`}
                    >Passwort
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">Keine User in diesem Mandanten.</p>
          )}

          {showAdd ? (
            <form onSubmit={addUser} className="border border-violet-200 bg-violet-50/40 p-4 space-y-3">
              {error && <div className="bg-red-50 border border-red-200 text-red-800 p-2 text-xs">{error}</div>}
              <h3 className="font-medium text-slate-900">Neuer Mandant-Admin</h3>
              <p className="text-xs text-slate-500 -mt-2">Passwort wird automatisch generiert und per E-Mail versendet.</p>
              <input type="text" required placeholder="Name" value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} className="w-full border-b border-slate-300 bg-transparent py-2 focus:border-slate-900 outline-none" data-testid="new-user-name" />
              <input type="email" required placeholder="E-Mail" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} className="w-full border-b border-slate-300 bg-transparent py-2 focus:border-slate-900 outline-none" data-testid="new-user-email" />
              <select value={form.role} onChange={(e) => setForm(p => ({ ...p, role: e.target.value }))} className="w-full border-b border-slate-300 bg-transparent py-2 focus:border-slate-900 outline-none">
                <option value="admin">Mandant-Admin</option>
                <option value="super_admin">Super-Admin</option>
              </select>
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowAdd(false)} className="flex-1 border border-gray-200 text-slate-900 py-2 text-sm hover:bg-gray-50">Abbrechen</button>
                <button type="submit" disabled={saving} className="flex-1 bg-violet-700 text-white py-2 text-sm hover:bg-violet-800 disabled:opacity-50" data-testid="new-user-submit">{saving ? '...' : 'Anlegen & E-Mail senden'}</button>
              </div>
            </form>
          ) : (
            <button onClick={() => setShowAdd(true)} className="w-full border-2 border-dashed border-violet-300 text-violet-700 py-3 hover:bg-violet-50 flex items-center justify-center gap-2" data-testid="show-add-user">
              <UserPlus weight="bold" className="w-4 h-4" /> User hinzufügen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


function OnboardingModal({ mandant, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [smtpResult, setSmtpResult] = useState(null);
  const [note, setNote] = useState('');
  const [noteEdit, setNoteEdit] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/mandanten/${mandant.id}/onboarding`);
      setData(res.data);
      setNote(res.data.note || '');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const updateFlag = async (payload) => {
    setSaving(Object.keys(payload)[0]);
    try {
      const res = await api.put(`/admin/mandanten/${mandant.id}/onboarding`, payload);
      setData(prev => ({ ...prev, ...res.data }));
      onSaved && onSaved();
    } catch (err) {
      alert('Fehler: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving('');
    }
  };

  const testSmtp = async () => {
    setTestingSmtp(true);
    setSmtpResult(null);
    try {
      const res = await api.post(`/admin/mandanten/${mandant.id}/onboarding/test-smtp`);
      setSmtpResult({ ok: true, msg: `Test-E-Mail gesendet an ${res.data.sent_to}` });
      await load();
      onSaved && onSaved();
    } catch (err) {
      setSmtpResult({ ok: false, msg: err.response?.data?.detail || err.message });
    } finally {
      setTestingSmtp(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="onboarding-modal">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <ListChecks weight="bold" className="w-6 h-6 text-violet-700" />
            <div>
              <h2 className="font-heading text-lg font-bold text-slate-900">Onboarding-Checkliste</h2>
              <p className="text-xs text-slate-500">{mandant.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600" data-testid="onboarding-close"><X weight="bold" className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center h-32"><Spinner weight="bold" className="w-8 h-8 text-violet-700 animate-spin" /></div>
          ) : data && (
            <>
              {/* Progress header */}
              <div className={`p-4 rounded-lg border-2 ${data.fertig ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`} data-testid="onboarding-progress">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-slate-900">
                    {data.fertig ? 'Onboarding abgeschlossen' : 'Onboarding läuft'}
                  </span>
                  <span className="font-mono font-bold text-lg">{data.done}/{data.total} · {data.progress}%</span>
                </div>
                <div className="h-3 bg-white/60 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${data.fertig ? 'bg-emerald-500' : 'bg-amber-500'}`}
                    style={{ width: `${data.progress}%` }}
                  />
                </div>
              </div>

              {/* Checklist */}
              <div className="space-y-2">
                {data.checks.map(c => {
                  const Icon = c.done ? CheckCircle : Circle;
                  const isManual = !c.auto && (c.key === 'avv_signed' || c.key === 'smtp_tested');
                  return (
                    <div
                      key={c.key}
                      className={`flex items-start gap-3 p-3 rounded-lg border ${c.done ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-200 bg-white'}`}
                      data-testid={`onboarding-check-${c.key}`}
                    >
                      <Icon weight={c.done ? 'fill' : 'regular'} className={`w-5 h-5 mt-0.5 flex-shrink-0 ${c.done ? 'text-emerald-600' : 'text-slate-300'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-sm font-medium ${c.done ? 'text-emerald-900' : 'text-slate-900'}`}>{c.label}</span>
                          {c.auto ? (
                            <span className="text-[10px] uppercase tracking-wide text-slate-400 border border-slate-200 rounded px-1.5 py-0.5">Auto</span>
                          ) : (
                            <span className="text-[10px] uppercase tracking-wide text-violet-700 border border-violet-200 rounded px-1.5 py-0.5">Manuell</span>
                          )}
                          {typeof c.count === 'number' && c.count > 0 && (
                            <span className="text-xs text-slate-500">({c.count})</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 flex items-start gap-1"><Info weight="regular" className="w-3 h-3 mt-0.5 flex-shrink-0" /> {c.hint}</p>
                        {isManual && (
                          <div className="mt-2 flex gap-2 flex-wrap items-center">
                            {c.key === 'avv_signed' && (
                              <>
                                <button
                                  disabled={saving === 'avv_signed'}
                                  onClick={() => updateFlag({ avv_signed: !c.done })}
                                  className={`text-xs px-3 py-1.5 rounded border font-medium disabled:opacity-50 ${c.done ? 'border-amber-300 text-amber-700 hover:bg-amber-50' : 'border-emerald-300 bg-emerald-600 text-white hover:bg-emerald-700'}`}
                                  data-testid="toggle-avv"
                                >
                                  {c.done ? 'Wieder öffnen' : 'Als unterzeichnet markieren'}
                                </button>
                                {data.avv_signed_at && (
                                  <span className="text-xs text-slate-500">am {new Date(data.avv_signed_at).toLocaleDateString('de-DE')}</span>
                                )}
                              </>
                            )}
                            {c.key === 'smtp_tested' && (
                              <>
                                <button
                                  disabled={testingSmtp}
                                  onClick={testSmtp}
                                  className="text-xs px-3 py-1.5 rounded border border-violet-300 bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 inline-flex items-center gap-1"
                                  data-testid="test-smtp"
                                >
                                  <PaperPlaneTilt weight="bold" className="w-3 h-3" /> {testingSmtp ? 'Sende…' : 'Test-E-Mail senden'}
                                </button>
                                {c.done && (
                                  <button
                                    disabled={saving === 'smtp_tested'}
                                    onClick={() => updateFlag({ smtp_tested: false })}
                                    className="text-xs px-2 py-1.5 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                                  >zurücksetzen</button>
                                )}
                                {data.smtp_tested_at && (
                                  <span className="text-xs text-slate-500">zuletzt {new Date(data.smtp_tested_at).toLocaleDateString('de-DE')}</span>
                                )}
                                {smtpResult && (
                                  <span className={`text-xs ${smtpResult.ok ? 'text-emerald-700' : 'text-red-700'}`}>{smtpResult.msg}</span>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Note */}
              <div className="border-t border-slate-200 pt-4">
                <label className="block text-xs uppercase tracking-wide font-bold text-slate-500 mb-1">Notiz</label>
                {noteEdit ? (
                  <div className="space-y-2">
                    <textarea
                      value={note}
                      onChange={e => setNote(e.target.value)}
                      rows={3}
                      className="w-full border border-slate-300 rounded-lg p-2 text-sm"
                      placeholder="Interne Notiz zu diesem Onboarding…"
                      data-testid="onboarding-note-input"
                    />
                    <div className="flex gap-2">
                      <button onClick={async () => { await updateFlag({ note }); setNoteEdit(false); }}
                              className="text-sm px-3 py-1.5 bg-violet-700 text-white rounded hover:bg-violet-800"
                              data-testid="onboarding-note-save">Speichern</button>
                      <button onClick={() => { setNote(data.note || ''); setNoteEdit(false); }}
                              className="text-sm px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-50">Abbrechen</button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-slate-700 bg-slate-50 rounded p-3 flex items-start justify-between gap-2">
                    <span className="whitespace-pre-wrap">{data.note || <span className="text-slate-400 italic">Keine Notiz</span>}</span>
                    <button onClick={() => setNoteEdit(true)} className="text-xs text-violet-700 hover:underline">Bearbeiten</button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
